import type {
  CardRarityEnum,
  Option,
  Prisma,
  SetCodeEnum,
  SubtypeEnum,
  VariantFinishEnum,
} from "@sorcery/db";
import {
  CardTypeEnum,
  ComparitorEnum,
  ComparitorFilterTypeEnum,
  ElementEnum,
  FilterTypeEnum,
  SetStatusEnum,
  cardTypeIncludeStatement,
  variantTypeIncludeStatement,
  variantTypeOrderByStatement,
} from "@sorcery/db";
import {
  escapeQueryForRelevanceScoring,
  escapeQueryForSearching,
} from "@sorcery/utils/server/search/full-text";

import { CARD_SORT_DEFAULT, CardSortEnum } from "~/types/card";
import { SEARCH_SORT } from "./filters";

export const DEFAULT_CARD_HEIGHT = 531;
export const DEFAULT_CARD_WIDTH = 380;

export const getCardWhere = (
  // override option value as we know it will always be a string during api filtering
  filters: (Option & { value: string })[],
  set: SetCodeEnum | "*",
  query?: string | null,
  scope?: string, // is this needed since collection limiting toggle was removed?
) => {
  const where = { AND: [] as Prisma.CardWhereInput[] };

  // Add collection limiting
  if (scope) {
    where.AND.push({
      collectionItems: {
        some: {
          collection: {
            user: {
              email: scope,
            },
          },
        },
      },
    });
  }

  // Add set filter
  where.AND.push({
    setCards: {
      some: {
        set: {
          status: SetStatusEnum.Released,
          code: set !== "*" ? set : undefined,
        },
      },
    },
  });

  //  Add search query
  if (query) {
    const sanitisedQuery = escapeQueryForSearching(query);
    where.AND.push({
      OR: [
        {
          name: { contains: sanitisedQuery },
        },
        {
          variants: {
            some: {
              vMeta: { typeText: { contains: sanitisedQuery } },
            },
          },
        },
        {
          guardian: {
            rulesText: { contains: sanitisedQuery },
          },
        },
      ],
    });
  }

  // Add filters
  if (filters.length) {
    // type filters
    const typeFilters = filters.filter((f) => f.type === FilterTypeEnum.TYPE);

    const filterAND = {
      AND: [] as Prisma.CardWhereInput[],
    };

    if (typeFilters.length) {
      filterAND.AND.push({
        guardian: {
          type: {
            in: typeFilters.map((f) => f.value as CardTypeEnum),
          },
        },
      });
    }

    // element filters
    const elementFilters = filters.filter((f) => f.type === FilterTypeEnum.ELEMENT);
    if (elementFilters.length) {
      filterAND.AND.push({
        elements: {
          some: {
            id: {
              in: elementFilters.map((f) => f.value.toLowerCase()),
            },
          },
        },
      });
    }

    // rarity filters
    const rarityFilters = filters.filter((f) => f.type === FilterTypeEnum.RARITY);
    if (rarityFilters.length) {
      filterAND.AND.push({
        guardian: {
          rarity: {
            in: rarityFilters.map((f) => f.value as CardRarityEnum),
          },
        },
      });
    }

    // finish filters
    const finishFilters = filters.filter((f) => f.type === FilterTypeEnum.FINISH);

    if (finishFilters.length) {
      const variantFilterWhere: Prisma.VariantWhereInput[] = [
        {
          OR: finishFilters.map((f) => ({
            finish: f.value as VariantFinishEnum,
          })),
        },
      ];

      if (set !== "*") {
        variantFilterWhere.push({
          setCard: {
            set: {
              code: set,
            },
          },
        });
      }
      filterAND.AND.push({
        variants: {
          some: {
            AND: variantFilterWhere,
          },
        },
      });
    }

    // subtype filters
    const subtypeFilters = filters.filter((f) => f.type === FilterTypeEnum.SUBTYPE);
    if (subtypeFilters.length) {
      filterAND.AND.push({
        subTypes: {
          some: {
            name: {
              in: subtypeFilters.map((f) => f.value as SubtypeEnum),
            },
          },
        },
      });
    }

    // keyword filters
    const keywordFilters = filters.filter((f) => f.type === FilterTypeEnum.KEYWORD);
    if (keywordFilters.length) {
      filterAND.AND.push({
        keywords: {
          some: {
            id: {
              in: keywordFilters.map((f) => f.value.toLowerCase()),
            },
          },
        },
      });
    }

    // artist filters
    const artistFilters = filters.filter((f) => f.type === FilterTypeEnum.ARTIST);
    if (artistFilters.length) {
      const variantFilterWhere: Prisma.VariantWhereInput = {
        artist: {
          slug: {
            in: artistFilters.map((f) => f.value),
          },
        },
      };
      if (set !== "*") {
        variantFilterWhere.setCard = {
          set: {
            code: set,
          },
        };
      }
      filterAND.AND.push({
        variants: {
          some: variantFilterWhere,
        },
      });
    }

    // power comparitor filter
    const powerComparitorFilter = filters.find((f) => f.type === ComparitorFilterTypeEnum.POWER);
    if (powerComparitorFilter) {
      filterAND.AND.push({
        guardian: {
          attack:
            powerComparitorFilter.comparitor === ComparitorEnum.EQUALS
              ? Number(powerComparitorFilter.value)
              : powerComparitorFilter.comparitor === ComparitorEnum.LESSTHAN
                ? { lt: Number(powerComparitorFilter.value) }
                : { gt: Number(powerComparitorFilter.value) },
        },
      });
    }

    // cost comparitor filter
    const costComparitorFilter = filters.find((f) => f.type === ComparitorFilterTypeEnum.COST);
    if (costComparitorFilter) {
      filterAND.AND.push({
        guardian: {
          cost:
            costComparitorFilter.comparitor === ComparitorEnum.EQUALS
              ? Number(costComparitorFilter.value)
              : costComparitorFilter.comparitor === ComparitorEnum.LESSTHAN
                ? { lt: Number(costComparitorFilter.value) }
                : { gt: Number(costComparitorFilter.value) },
        },
      });
    }

    // threshold comparitor filter
    const thresholdComparitorFilter = filters.find(
      (f) => f.type === ComparitorFilterTypeEnum.THRESHOLD,
    );
    if (thresholdComparitorFilter) {
      const f: Prisma.CardWhereInput = {};
      const comparitor =
        thresholdComparitorFilter.comparitor === ComparitorEnum.EQUALS
          ? Number(thresholdComparitorFilter.target)
          : thresholdComparitorFilter.comparitor === ComparitorEnum.LESSTHAN
            ? { lt: Number(thresholdComparitorFilter.target) }
            : { gt: Number(thresholdComparitorFilter.target) };

      if (thresholdComparitorFilter.value === "*") {
        const thresholdsToCheck = Object.values(ElementEnum).filter((v) => v !== ElementEnum.None);
        f.OR = thresholdsToCheck.map((t) => ({
          AND: [
            {
              elements: {
                some: {
                  id: t.toLowerCase(),
                },
              },
              guardian: {
                [`${t.toLowerCase()}Threshold`]: comparitor,
              },
            },
          ],
        })) as Prisma.CardWhereInput[];
      } else {
        f.OR = [
          {
            AND: [
              {
                guardian: {
                  [`${thresholdComparitorFilter.value.toLowerCase()}Threshold`]: comparitor,
                },
                elements: {
                  some: {
                    id: thresholdComparitorFilter.value.toLowerCase(),
                  },
                },
              },
            ],
          },
        ];
      }

      // account for target = zero or any "less than" value including the artifacts
      const includeArtifacts = !comparitor || !!(comparitor as any).lt;
      if (includeArtifacts) {
        f.OR.push({
          guardian: {
            type: CardTypeEnum.Artifact,
          },
        });
      }

      // add threshold filter
      filterAND.AND.push(f);
    }

    where.AND.push(filterAND);
  }

  return where;
};

// variant reducing (when in collection mode)
export const getCardVWhere = (
  // override option value as we know it will always be a string during api filtering
  filters: (Option & { value: string })[],
  set: SetCodeEnum | "*",
) => {
  const vWhere = [] as Prisma.VariantWhereInput[];
  const limitToSetVariants = set !== "*";
  if (limitToSetVariants) {
    vWhere.push({
      setCard: {
        set: {
          code: set,
        },
      },
    });
  }
  const finishFilters = filters.filter((f) => f.type === FilterTypeEnum.FINISH);
  const limitToFinishVariants = !!finishFilters.length;
  if (limitToFinishVariants) {
    vWhere.push({
      finish: {
        in: finishFilters.map((f) => f.value as VariantFinishEnum),
      },
    });
  }

  return vWhere;
};

export const getCardInclude = (vWhere: Prisma.VariantWhereInput[]) => {
  return vWhere.length
    ? {
        ...cardTypeIncludeStatement,
        variants: {
          where: { AND: vWhere },
          include: variantTypeIncludeStatement,
          orderBy: variantTypeOrderByStatement,
        },
      }
    : cardTypeIncludeStatement;
};

export const getCardOrderBy = (sort?: CardSortEnum | typeof SEARCH_SORT, query?: string | null) => {
  // default to name ordering or relevance if there's a search term
  const defaultSort = query ? SEARCH_SORT : CARD_SORT_DEFAULT;
  const baseSort = getSort(sort ?? defaultSort, query);
  const tiebreaker = getSort(CARD_SORT_DEFAULT);

  return [...baseSort, ...tiebreaker] satisfies Prisma.CardOrderByWithRelationInput[];
};

// helper for ordering
const getSort = (
  sort: CardSortEnum | typeof SEARCH_SORT,
  query?: string | null,
): Prisma.CardOrderByWithRelationInput[] => {
  switch (sort) {
    case SEARCH_SORT:
      return [
        {
          _relevance: {
            fields: "name",
            search: query ? escapeQueryForRelevanceScoring(query) : "",
            sort: "desc",
          },
        },
        {
          guardian: {
            _relevance: {
              fields: "rulesText",
              search: query ? escapeQueryForRelevanceScoring(query) : "",
              sort: "desc",
            },
          },
        },
        // impossible now the typeText is stored on the variant
        // {
        //   guardian: {
        //     _relevance: {
        //       fields: "typeText",
        //       search: query ? escapeQueryForRelevanceScoring(query) : "",
        //       sort: "desc",
        //     },
        //   },
        // },
      ];
    case CardSortEnum.NAME:
      return [{ name: "asc" }];
    case CardSortEnum.ALLTIME:
      return [
        {
          deckCards: { _count: "desc" },
        },
        {
          avatarCards: { _count: "desc" },
        },
      ];
    case CardSortEnum.TRENDING:
      return [{ hotscore: "desc" }];
    case CardSortEnum.POWER:
      return [{ guardian: { attack: { sort: "asc", nulls: "last" } } }];
    case CardSortEnum.COST:
      return [{ guardian: { cost: { sort: "asc", nulls: "last" } } }];
    default:
      throw new Error(`Invalid sort type: ${sort as string}`);
  }
};
